/**
 * Access & Benefits — client read model.
 *
 * Reads the signed-in learner's eligibility from the database (never from local
 * state) and asks the Access & Benefits Engine which benefit should apply. The
 * engine decides; this hook only fetches and caches.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  eligibilityFromRpc,
  emptyEligibility,
  resolveBenefits,
  type BenefitEligibility,
  type EligibilityRpcPayload,
} from "@/pricing/access-benefits";
import type { RegionCode } from "@/pricing";

export interface UseBenefitsResult {
  loading: boolean;
  eligibility: BenefitEligibility;
  /**
   * The benefit id that should apply with no code typed — an approved
   * scholarship, partner or institution entitlement. Null when there is none.
   */
  autoBenefitId: string | null;
  autoBenefitLabel: string | null;
  refresh: () => Promise<void>;
}

export function useBenefits(region: RegionCode = "IN"): UseBenefitsResult {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [eligibility, setEligibility] = useState<BenefitEligibility>(() =>
    emptyEligibility(),
  );
  const requested = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setEligibility(emptyEligibility());
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_my_benefit_eligibility");
      if (error) throw error;
      setEligibility(
        eligibilityFromRpc(data as EligibilityRpcPayload | null, user.email ?? null),
      );
    } catch {
      // A benefits read must never block a checkout — fall back to no benefits.
      setEligibility(emptyEligibility({ userId: user.id, email: user.email ?? null }));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    const key = user?.id ?? null;
    if (requested.current === key) return;
    requested.current = key;
    void load();
  }, [user?.id, load]);

  const decision = useMemo(
    () => resolveBenefits({ eligibility, region }),
    [eligibility, region],
  );

  const auto = decision.applied;

  return {
    loading,
    eligibility,
    autoBenefitId: auto && auto.source === "grant" ? auto.benefitId : null,
    autoBenefitLabel: auto && auto.source === "grant" ? auto.rule.label : null,
    refresh: load,
  };
}
